/**
 * swisstopo profile service client (SPEC §8.1 step 4, PROTOCOLS.md §7.1).
 * POST https://api3.geo.admin.ch/rest/services/profile.json?sr=2056&distinct_points=true&nb_points=N
 * with a GeoJSON LineString body. Limit ~5000 coordinates → we chunk at 2000 (1-point overlap).
 * CORS is open; if the direct call fails at the network level we retry through the Worker (/api/elevation).
 */
import { db } from '@/db/db';

export const PROFILE_URL = 'https://api3.geo.admin.ch/rest/services/profile.json';
export const PROXY_URL = '/api/elevation';
export const CHUNK_POINTS = 2000;

interface ProfilePoint {
  dist: number;
  alts: { COMB?: number; DTM2?: number; DTM25?: number };
  easting: number;
  northing: number;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

async function postProfile(
  url: string,
  coords: [number, number][],
  fetcher: FetchLike,
  signal?: AbortSignal,
): Promise<ProfilePoint[]> {
  const q = `sr=2056&distinct_points=true&nb_points=${coords.length}`;
  const res = await fetcher(`${url}?${q}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'LineString', coordinates: coords }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`swisstopo profile HTTP ${res.status}`);
  const data = (await res.json()) as ProfilePoint[];
  if (!Array.isArray(data) || data.length === 0) throw new Error('swisstopo profile: empty response');
  return data;
}

/** Interpolate the profile (by service distance) at the planar chunk distances of our input points. */
export function mapProfile(profile: ProfilePoint[], chunkDist: number[]): number[] {
  const alt = (p: ProfilePoint) => p.alts.COMB ?? p.alts.DTM2 ?? p.alts.DTM25 ?? NaN;
  let j = 0;
  return chunkDist.map((d) => {
    while (j < profile.length - 2 && profile[j + 1]!.dist < d) j++;
    const a = profile[j]!;
    const b = profile[j + 1] ?? a;
    const span = b.dist - a.dist;
    const t = span > 0 ? Math.max(0, Math.min(1, (d - a.dist) / span)) : 0;
    return alt(a) + (alt(b) - alt(a)) * t;
  });
}

export interface ElevationOptions {
  fetcher?: FetchLike;
  signal?: AbortSignal;
  onProgress?: (fraction: number) => void;
  /** Pause between chunk requests (fair use). */
  delayMs?: number;
}

/** Elevation for each LV95 point, from swisstopo. Throws if the service is unreachable (caller falls back). */
export async function fetchSwisstopoElevations(
  e: number[],
  n: number[],
  opts: ElevationOptions = {},
): Promise<number[]> {
  const fetcher = opts.fetcher ?? ((u, i) => fetch(u, i));
  const out = new Array<number>(e.length).fill(NaN);
  let useProxy = false;
  const chunks: [number, number][] = [];
  for (let s = 0; s < e.length - 1; s += CHUNK_POINTS - 1)
    chunks.push([s, Math.min(e.length - 1, s + CHUNK_POINTS - 1)]);
  if (chunks.length === 0) chunks.push([0, e.length - 1]);
  for (let c = 0; c < chunks.length; c++) {
    const [a, b] = chunks[c]!;
    const coords: [number, number][] = [];
    const cd: number[] = [];
    let acc = 0;
    for (let i = a; i <= b; i++) {
      if (i > a) acc += Math.hypot(e[i]! - e[i - 1]!, n[i]! - n[i - 1]!);
      coords.push([Math.round(e[i]! * 100) / 100, Math.round(n[i]! * 100) / 100]);
      cd.push(acc);
    }
    let profile: ProfilePoint[];
    try {
      profile = await postProfile(useProxy ? PROXY_URL : PROFILE_URL, coords, fetcher, opts.signal);
    } catch (err) {
      if (useProxy || !(err instanceof TypeError)) throw err;
      // Network/CORS failure → retry through our Worker proxy.
      useProxy = true;
      profile = await postProfile(PROXY_URL, coords, fetcher, opts.signal);
    }
    const h = mapProfile(profile, cd);
    for (let i = a; i <= b; i++) out[i] = h[i - a]!;
    opts.onProgress?.((c + 1) / chunks.length);
    if (opts.delayMs && c < chunks.length - 1) await new Promise((r) => setTimeout(r, opts.delayMs));
  }
  if (out.some((x) => !Number.isFinite(x))) throw new Error('swisstopo profile: missing values');
  return out;
}

export async function cachedElevations(hash: string): Promise<number[] | null> {
  try {
    return (await db.elevationCache.get(hash))?.elevations ?? null;
  } catch {
    return null;
  }
}

export async function storeElevations(hash: string, elevations: number[]): Promise<void> {
  try {
    await db.elevationCache.put({ hash, elevations, createdAt: Date.now() });
  } catch {
    /* cache is best effort */
  }
}
